import React, { useState, useEffect, useCallback, useRef } from "react";
import Tree from "react-d3-tree";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  deleteDoc,
} from "firebase/firestore";
import FamilyForm from "./FamilyForm";

function FamilyTree({ user }) {
  const [members, setMembers] = useState([]);
  const [treeData, setTreeData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [modalMember, setModalMember] = useState(null);
  const containerRef = useRef(null);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });

  const fetchMembers = useCallback(async () => {
    if (!user) return;
    const q = query(
      collection(db, "FamilyTree"),
      where("ownerId", "==", user.uid)
    );
    const snapshot = await getDocs(q);
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setMembers(data);
  }, [user]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);
  
  // Effect to center the tree
  useEffect(() => {
    if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        setTranslate({ x: width / 2, y: 100 });
    }
  }, [members]); // Recalculate when members change

  // Build nested tree
  useEffect(() => {
    if (!members.length) {
      setTreeData(null);
      return;
    }
    const map = {};
    members.forEach((m) => {
      map[m.id] = { ...m, children: [] };
    });
    let root = null;
    members.forEach((m) => {
      if (m.parentId && map[m.parentId]) {
        map[m.parentId].children.push(map[m.id]);
      } else {
        root = map[m.id];
      }
    });
    setTreeData(root);
  }, [members]);

  // ✅ CHANGE 1: Updated handleDelete to check for children
  const handleDelete = async (nodeToDelete) => {
    // Check if the node has children. The 'children' array is populated when the tree is built.
    if (nodeToDelete.children && nodeToDelete.children.length > 0) {
      alert("Cannot delete this member as they have children. Please remove the children first.");
      return; // Stop the function here
    }

    if (window.confirm(`Are you sure you want to delete ${nodeToDelete.name}?`)) {
      await deleteDoc(doc(db, "FamilyTree", nodeToDelete.id));
      fetchMembers();
    }
  };

  const renderCustomNode = ({ nodeDatum }) => {
    const radius = 40;
    const rectWidth = nodeDatum.name.length * 8 * 1.8;
    const rectHeight = 25;

    return (
      <g>
        <g
            className="cursor-pointer hover:scale-110 transition-transform duration-200"
            onClick={() => setModalMember(nodeDatum)}
        >
            {nodeDatum.photoUrl ? (
            <image
                href={nodeDatum.photoUrl}
                x={-radius}
                y={-radius}
                width={radius * 2}
                height={radius * 2}
                clipPath="url(#clipCircle)"
            />
            ) : (
            <circle r={radius} fill="#4f46e5" stroke="#1f2937" strokeWidth={2} />
            )}
            <defs>
                <clipPath id="clipCircle"><circle r={radius} /></clipPath>
            </defs>
            <rect
            x={-rectWidth / 2}
            y={radius + 2}
            width={rectWidth}
            height={rectHeight}
            rx={12}
            ry={12}
            fill="#5c9474"
            />
            <text
            y={radius + 20}
            textAnchor="middle"
            >
            {nodeDatum.name}
            </text>
            <text
            x={0}
            y={radius + 48}
            textAnchor="middle"
            fill="#1f2937"
            style={{ fontSize: "12px", fontStyle: "italic", pointerEvents: "none", color:"white" }}
            >
            {nodeDatum.dob}
            </text>
        </g>
        <foreignObject x={radius + 10} y={-20} width={80} height={50}>
            <div className="flex flex-col gap-1">
            <button
                className="bg-yellow-400 hover:bg-yellow-500 text-xs px-2 py-1 rounded shadow"
                onClick={(e) => {
                e.stopPropagation();
                setSelectedMemberId(nodeDatum.id);
                setShowForm(true);
                }}
            >
                ✏ Edit
            </button>
            {/* ✅ CHANGE 2: Pass the entire nodeDatum object to handleDelete */}
            <button
                className="bg-red-500 hover:bg-red-600 text-xs px-2 py-1 rounded shadow text-white"
                onClick={(e) => {
                e.stopPropagation();
                handleDelete(nodeDatum); 
                }}
            >
                🗑 Delete
            </button>
            </div>
        </foreignObject>
      </g>
    );
  };

  return (
    // ✨ BONUS FIX: Restored proper flexbox layout for the component
  <div className="w-full flex flex-col h-[75vh]">
      {!showForm && (
        <div className="flex justify-center my-4">
          <button
            onClick={() => {
              setSelectedMemberId(null);
              setShowForm(true);
            }}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-xl shadow-lg transition-colors"
          >
            + Add Family Member
          </button>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50 p-4">
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-lg p-6">
            <FamilyForm
              memberId={selectedMemberId}
              onSave={() => {
                setShowForm(false);
                fetchMembers();
              }}
              user={user}
            />
          </div>
        </div>
      )}

      {/* ✨ BONUS FIX: Container now correctly fills available space */}
      <div
        ref={containerRef}
        className="flex-1 w-full h-full p-6 bg-gradient-to-b from-indigo-50 to-red-100 rounded-3xl shadow-inner"
      >
        {treeData ? (
          <Tree
            data={treeData}
            orientation="vertical"
            translate={translate}
            zoomable
            separation={{ siblings: 2, nonSiblings: 3 }}
            nodeSize={{ x: 250, y: 220 }}
            renderCustomNodeElement={renderCustomNode}
          />
        ) : (
          <p className="text-gray-500 text-center mt-10 text-lg">
            No family members yet. Click “Add Family Member” to start.
          </p>
        )}
      </div>

     {modalMember && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
            {/* ✅ CHANGE 2: Modal width is now wider */}
            <div className="bg-white rounded-2xl shadow-lg w-full max-w-2xl max-h-full overflow-y-auto relative flex flex-col">
                <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-800" onClick={() => setModalMember(null)}>
                ✖
                </button>
                <div className="p-8 flex-1">
                {modalMember.photoUrl && (
                    <img src={modalMember.photoUrl} alt={modalMember.name} className="w-32 h-32  mx-auto mb-4 object-cover" />
                )}
                <h2 className="text-2xl font-bold mb-1 text-center">{modalMember.name}</h2>
                {modalMember.dob && <p className="text-sm italic text-center mb-4">{modalMember.dob}</p>}
                
                {/* ✅ CHANGE 1: Text is now justified */}
                <div 
                    className="prose prose-sm max-w-none text-justify mb-4" 
                    dangerouslySetInnerHTML={{ __html: modalMember.bio || "<p>No bio available.</p>" }} 
                />
                
                </div>
                <div className="p-4 bg-gray-50 border-t flex justify-center rounded-b-3xl">
                <button className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg shadow transition-colors" onClick={() => setModalMember(null)}>
                    Close
                </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
}

export default FamilyTree;